import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { AIExpert } from '@garbera/shared';
import { EXPERT_AVATARS } from '../../types/ai';

interface WelcomeScreenProps {
  experts: AIExpert[];
  onExpertSelect: (expert: AIExpert) => void;
}

/**
 * ChatGPT/Geminiライクなウェルカム画面
 */
export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  experts,
  onExpertSelect,
}) => {
  const theme = useTheme();

  // 専門家のアバターを取得（手動設定を優先、なければ名前から推測）
  const getExpertAvatar = (expert: AIExpert): string => {
    // 手動設定されたアバターがあればそれを使用
    if (expert.avatarUrl) {
      return expert.avatarUrl;
    }
    
    // なければ名前から推測
    const lowercaseName = expert.name.toLowerCase();
    
    if (lowercaseName.includes('不動産') || lowercaseName.includes('営業')) {
      return EXPERT_AVATARS.real_estate_sales;
    } else if (lowercaseName.includes('法務') || lowercaseName.includes('法律')) {
      return EXPERT_AVATARS.legal;
    } else if (lowercaseName.includes('税務') || lowercaseName.includes('税金')) {
      return EXPERT_AVATARS.tax;
    }
    return EXPERT_AVATARS.default;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh', // 100vhを確保してフルスクリーン表示
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      }}
    >
      {/* ヘッダーセクション */}
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          px: 3,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: 48,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
        </Box>
        
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          ガクフドキャリア
        </Typography>
        
        <Typography
          variant="h5"
          sx={{
            fontWeight: 400,
            color: theme.palette.text.secondary,
            mb: 1,
          }}
        >
          お手伝いできることはありますか？
        </Typography>
        
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          専門家AIを選択して、不動産に関する質問やご相談をお聞かせください
        </Typography>
      </Box>

      {/* 専門家AI選択セクション */}
      <Box sx={{ px: 3, mb: 6, flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            mb: 3,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          専門家AIを選択
        </Typography>
        
        <Grid container spacing={2} justifyContent="center">
          {experts.map((expert) => {
            // 専門家の数に基づいてレスポンシブなグリッドサイズを計算
            const getGridSizes = (count: number) => {
              if (count === 1) {
                return { xs: 12, sm: 8, md: 6, lg: 4 }; // 中央寄せで適度なサイズ
              } else if (count === 2) {
                return { xs: 12, sm: 6, md: 6, lg: 6 }; // 2つ並び
              } else if (count === 3) {
                return { xs: 12, sm: 6, md: 4, lg: 4 }; // 3つ並び
              } else if (count === 4) {
                return { xs: 12, sm: 6, md: 6, lg: 3 }; // 2x2または4並び
              } else {
                return { xs: 12, sm: 6, md: 4, lg: 3 }; // 3つまたは4つ並び
              }
            };
            
            const gridSizes = getGridSizes(experts.length);
            
            return (
              <Grid 
                item 
                xs={gridSizes.xs} 
                sm={gridSizes.sm} 
                md={gridSizes.md} 
                lg={gridSizes.lg}
                key={expert.id}
              >
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  height: 280, // カード全体の高さを固定
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                    borderColor: theme.palette.primary.main,
                  },
                }}
                onClick={() => onExpertSelect(expert)}
              >
                <CardContent sx={{ 
                  textAlign: 'center', 
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontSize: '1.5rem',
                    }}
                  >
                    {getExpertAvatar(expert)}
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      fontSize: '1rem',
                    }}
                  >
                    {expert.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      fontSize: '0.875rem',
                      lineHeight: 1.4,
                      flex: 1, // 残りのスペースを占有
                      display: 'flex',
                      alignItems: 'center', // テキストを中央寄せ
                      minHeight: 0, // フレックスアイテムの最小高さを0に設定
                    }}
                  >
                    {expert.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* フッター */}
      <Box
        sx={{
          textAlign: 'center',
          py: 3,
          px: 3,
          marginTop: 'auto', // 自動的に下部に配置
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          flexShrink: 0, // サイズ固定
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            fontSize: '0.75rem',
          }}
        >
          AIとの対話を通して、不動産の知識を深めましょう
        </Typography>
      </Box>
    </Box>
  );
};

export default WelcomeScreen; 